import { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface JsonNodeProps {
  label?: string;
  value: unknown;
  depth?: number;
}

function JsonNode({ label, value, depth = 0 }: JsonNodeProps) {
  const [expanded, setExpanded] = useState(depth < 2);
  const isObject = value && typeof value === 'object';

  if (!isObject) {
    const color = typeof value === 'string' ? 'text-semantic-up'
      : typeof value === 'number' ? 'text-primary'
      : typeof value === 'boolean' ? 'text-accent-yellow'
      : 'text-muted';
    return (
      <div className="flex items-baseline gap-2" style={{ paddingLeft: depth * 16 }}>
        {label && <span className="text-primary text-xs">{label}:</span>}
        <span className={`text-xs ${color}`}>{JSON.stringify(value)}</span>
      </div>
    );
  }

  const entries = Array.isArray(value) ? value.map((v, i) => [i, v] as [number, unknown]) : Object.entries(value as Record<string, unknown>);

  return (
    <div style={{ paddingLeft: depth * 16 }}>
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-body hover:text-ink">
        <ChevronRightIcon className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        {label && <span className="text-primary">{label}</span>}
        <span className="text-muted">
          {Array.isArray(value) ? `[${value.length}]` : `{${Object.keys(value as object).length}}`}
        </span>
      </button>
      {expanded && entries.map(([k, v]) => <JsonNode key={k} label={String(k)} value={v} depth={depth + 1} />)}
    </div>
  );
}

interface JsonViewerProps {
  data: unknown;
  className?: string;
}

export default function JsonViewer({ data, className = '' }: JsonViewerProps) {
  if (data === null || data === undefined) return <span className="text-xs text-muted">null</span>;
  return (
    <div className={`font-mono bg-canvas rounded-lg p-3 overflow-auto max-h-96 border border-hairline ${className}`}>
      <JsonNode value={data} depth={0} />
    </div>
  );
}